
import {
    EzObject,
    EzBoolean,
    EzString,
    EzArray
} from '/ezlibrary/helpers/EzHelpers.js';
import { EzClockerAccountType } from '/ezlibrary/EzClockerAccountType.js';
import { EzJSONSerializable } from '/ezlibrary/entities/EzJSONSerializable.js';

/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    1) Do not import EzUserRole in this file to avoid circular dependencies
    1) Do not import EzAccountRole in this file to avoid circular dependencies
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

/**
 * @class
 * @extends {EzJSONSerializable}
 * @description
 * Represents an ezClocker user's role. Used in the EzUserRole enumeration.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *  import { EzClockerRole } from '/ezlibrary/EzClockerRole.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzClockerRole extends EzJSONSerializable {
    /**
     * @public @constructor
     * Creates an instance of EzClockerRole
     * @param {string} [ezAccountRole='ROLE_ANONYMOUS']
     * A valid enumn property value from EzAccountRole
     * Important: Must also equal ezuserRole param.
     * @param {string} [ezUserRole='ROLE_ANONYMOUS']
     * A valid enumn property value from EzUserRole
     * Important: Must also equal ezuserRole param.
     * @param {string} [authorityName='ROLE_ANONYMOUS']
     * The authority name of the role. This is the value returned by the API and should be used when checking a user's role.
     * Important: Must also equal ezuserRole param.
     * @param {string} [ezClockerAccountType = EzClockerAccountType.ANONYMOUS]
     * A valid enum property value from EzClockerAccountType representing the account type for the EzClockerRole. This is used for display purposes and is
     * not returned by the API.
     * @param {string} [roleName='Anonymous']
     * Display name of the role
     * @param {string} [contextName='anonymousAccountContext']
     * Context name of the role
     * @param {array} [accessInfo=EzArray.EMPTY]
     * List of strings describing the access level of the role. This is used for display purposes and is not returned by the API.
     * @param {boolean} [internalOnly=false]
     * Whether the role is for internal use only. This is used for display purposes and is not returned by the API.
     * @param {string} [legacyAccountType='anonymous']
     * Legacy account type for the EzClockerRole (if any)
     * @param {array} [enabledPermissions=EzArray.EMPTY]
     * List of permissions that are enabled by default for the role. This is used for display purposes and is not returned by the API.
     * @param {array} [disabledPermissions=EzArray.EMPTY]
     * List of permissions that are disabled by default for the role. This is used for display purposes and is not returned by the API.
     */
    constructor(
        ezAccountRole = 'ROLE_ANONYMOUS',
        ezUserRole = 'ROLE_ANONYMOUS',
        authorityName = 'ROLE_ANONYMOUS',
        ezClockerAccountType = EzClockerAccountType.ANONYMOUS,
        roleName = 'Anonymous',
        contextName = 'anonymousContext',
        propName = 'anonymous',
        accessInfo = EzArray.EMPTY,
        internalOnly = false,
        legacyAccountType = 'anonymous',
        enabledPermissions = EzArray.EMPTY,
        disabledPermissions = EzArray.EMPTY) {
        super();

        this.#ezAccountRole = EzString.stringWithTextOrDefault(
            ezAccountRole,
            'ROLE_ANONYMOUS');

        this.#ezUserRole = EzString.stringWithTextOrDefault(
            ezUserRole,
            'ROLE_ANONYMOUS');

        this.#authorityName = EzString.stringWithTextOrDefault(
            authorityName,
            'ROLE_ANONYMOUS');

        if (this.ezAccountRole !== this.ezUserRole || this.ezAccountRole !== this.authorityName) {
            throw new EzBadParamException(
                { ezAccountRole },
                this,
                'constructor',
                EzString.em`
                    The provided ezAccountRole param must be identical to the provided ezUserRole and
                    authorityName parameters (for backwards compatibility support).`);
        }

        this.ezClockerAccountType = EzString.stringWithTextOrDefault(
            ezClockerAccountType,
            EzClockerAccountType.ANONYMOUS);

        if ('UNKNOWN' === this.authorityName) {
            this.#roleName = 'Anonymous';

            this.#accessInfo = [
                'Read access to ezClocker unsecured public website pages.',
                'Read acess to the ezClocker Blog Website.',
                'No access to Employer Dashboard website page.',
                'No access to Employer Schedules website page.',
                'No access to Employer Archive website page.',
                'No access to Employer Account website page.',
                'No access to the Employee Dashboard website page.',
                'No access to the Employee Schedule website page.',
                'No access to the Personal Dashboard website page',
                'No access to the Developer Account website page.',
                'No access to the Administration Portal website page.',
                'No access to the Employer mobile app.',
                'No access to the Employee mobile app.',
                'No access to the Personal mobile app.'
            ];

            this.#internalOnly = false;
        } else {
            this.#roleName = EzString.stringWithTextOrDefault(
                roleName,
                EzString.upperCaseFirstLetter(
                    this.authorityName.replace('ROLE_', '').toLowerCase()));

            this.#accessInfo = EzArray.arrayOrDefault(
                accessInfo,
                [
                    'Read access to ezClocker unsecured public website pages.',
                    'Read acess to the ezClocker Blog Website.'
                ]);

            this.#internalOnly = EzBoolean.booleanOrFalse(internalOnly);
        }

        const roleNameLowerCase = this.roleName.toLowerCase();

        this.#contextName = EzString.stringWithTextOrDefault(
            contextName.endsWith('Context')
                ? contextName
                : `${contextName}Context`,
            `${roleNameLowerCase}Context`);

        this.#propName = EzString.stringWithTextOrDefault(
            propName,
            roleNameLowerCase);

        this.#legacyAccountType = EzString.stringWithTextOrDefault(
            legacyAccountType,
            roleNameLowerCase);

        this.#ezDefaultEnabledPermissions = EzArray.arrayOrEmpty(enabledPermissions);

        this.#ezDefaultDisabledPermissions = EzArray.arrayOrEmpty(disabledPermissions);
    }



    /**
     * @private @field
     * Stores the the EzAccountRole for the account
     * @type {string}
     * A valid enum property value from EzAccountRole
     */
    #ezAccountRole = 'ROLE_ANONYMOUS';
    /**
     * @public @readonly @property
     * Gets the EzAccountRole for the account
     * A valid enum property value from EzAccountRole
     * @returns {string}
     */
    get ezAccountRole() {
        return this.#ezAccountRole;
    }

    /**
     * @private @field
     * Stores the the EzUserRole for the account
     * @type {string}
     * A valid enum property value from EzUserRole
     */
    #ezUserRole = 'ROLE_ANONYMOUS';
    /**
     * @public @readonly @property
     * Gets the EzUserRole for the account
     * A valid enum property value from EzUserRole
     * @returns {string}
     */
    get ezUserRole() {
        return this.#ezUserRole;
    }

    /**
     * @public @field
     * Stores the authority role name for the account.
     * This is the value returned by the API and should be used when checking a user's role.
     * @returns {string}
     * A valid enum property value from EzAccountRole or EzUserRole (legacy).
     */
    #authorityName = 'ROLE_ANONYMOUS';
    /**
     * @public @readonly @property
     * Gets the authority role name.
     * This is the value returned by the API and should be used when checking a user's role.
     * @returns {string}
     * A valid enum property value from EzAccountRole or EzUserRole (legacy).
     */
    get authorityName() {
        return this.#authorityName;
    }
    /**
     * @public @function
     * Gets the authority name.
     * This is the value returned by the API and should be used when checking a user's role.
     * @returns {string}
     * @deprecated
     * Migrate to property: EzClockerRole.authorityName
     */
    ezGetAuthorityName() {
        return this.authorityName;
    }

    /**
     * @private @field
     * Stores the EzClockerAccountType for the EzClockerRole
     */
    #ezClockerAccountType = EzClockerAccountType.ANONYMOUS;
    /**
     * @public @readonly @property
     * Gets tthe EzClockerAccountType for the EzClockerRole
     * @returns {string}
     * A valid enum property value from EzClockerAccountType.
     */
    get ezClockerAccountType() {
        return this.#ezClockerAccountType;
    }
    /**
     * @public @function
     * Gets the EzClockerAccountType for the EzClockerRole
     * @returns {string}
     * A valid enum property value from EzClockerAccountType.
     * @deprecated
     * Migrate to property: EzClockerRole.ezClockerAccountType
     */
    ezClockerAccountType() {
        return this.ezClockerAccountType;
    }

    /**
     * @public @field
     * Stores the display role name.
     * This is not returned by the API and should not be used for checking a user's role.
     * @returns {string}
     */
    #roleName = 'Anonymous';
    /**
     * @public @readonly @property
     * Gets the display role name.
     * This is not returned by the API and should not be used for checking a user's role.
     * @returns {string}
     */
    get roleName() {
        return this.#roleName;
    }
    /**
     * @public @function
     * Gets the role name
     * @returns {string}
     * @deprecated
     * Migrate to property: EzClockerRole.roleName
     */
    ezGetRoleName() {
        return this.roleName;
    }

    /**
     * @private @field
     * Stores the role/accounts context name
     * @type {string}
     */
    #propName = 'anonymous';
    /**
     * @public @readonly @property
     * Gets the base property name for the EzClockerRole
     * Normally is the lowercase of roleName, but can be set to a custom value if needed. This is used for mapping the EzClockerRole to the
     * corresponding property in EzUserRole.
     * @returns {string}
     */
    get propName() {
        return this.#propName;
    }

    /**
     * @private @field
     * Stores the role/accounts context name
     * @type {string}
     */
    #contextName = 'anonymousContext';
    /**
     * @public @readonly @property
     * Gets the role/accounts context name
     * @returns {string}
     */
    get contextName() {
        return this.#contextName;
    }

    /**
     * @public @field
     * Stores the access level description of the role for display purposes. This is not returned by the API and should not be used for checking a user's
     * access level.
     * @returns {array}
     */
    #accessInfo = [
        'Read access to ezClocker unsecured public website pages.',
        'Read acess to the ezClocker Blog Website.'
    ];
    /**
     * @public @readonly @property
     * Gets the access level description of the role for display purposes. This is not returned by the API and should not be used for checking a user's
     * access level.
     * @returns {array}
     */
    get accessInfo() {
        return this.#accessInfo;
    }
    /**
     * @public @function
     * Gets the user role description
     * @returns {array}
     * @deprecated
     * Migrate to property: EzClockerRole.accessInfo
     */
    ezGetAccessInfo() {
        return this.accessInfo;
    }

    /**
     * @public @field
     * Stores if the role is for internal use only or not
     * @returns {boolean}
     */
    #internalOnly = false;
    /**
     * @public @readonly @property
     * Gets if the role is for internal use only or not
     * @returns {boolean}
     */
    get internalOnly() {
        return this.#internalOnly;
    }
    /**
     * @public @function
     * Gets if the role is internal use only
     * @returns {boolean}
     * @deprecated
     * Migrate to property: EzClockerRole.internalOnly
     */
    ezGetInternalOnly() {
        return this.internalOnly;
    }

    /**
     * @public @field
     * Stores the legacy account type for the role (if any)
     * @returns {string}
     */
    #legacyAccountType = 'anonymous';
    /**
     * @public @readonly @property
     * the legacy account type for the role (if any)
     * @returns {string}
     */
    get legacyAccountType() {
        return this.#legacyAccountType;
    }
    /**
     * @public @function
     * Gets the legacy account type
     * @returns {string}
     * @deprecated
     * Migrate to property: EzClockerRole.legacyAccountType
     */
    ezGetLegacyAccountType() {
        return this.legacyAccountType;
    }

    /**
     * @public @field
     * Stores an array of the default enabled permissions for this role.
     * @returns {array}
     */
    #ezDefaultEnabledPermissions = EzArray.EMPTY;
    /**
     * @public @readonly @property
     * Gets an array of the default enabled permissions for this role.
     * @returns {array}
     */
    get ezDefaultEnabledPermissions() {
        return this.#ezDefaultEnabledPermissions;
    }
    /**
     * @public @function
     * Gets the list of default enabled permissions for this role.
     * @returns {array}
     * @deprecated
     * Migrate to property: EzClockerRole.ezDefaultEnabledPermissions
     */
    ezGetDefaultEnabledPermissions() {
        return this.ezDefaultEnabledPermissions;
    }

    /**
     * @public @field
     * Stores an array of the default disabled permissions
     * @returns {array}
     */
    #ezDefaultDisabledPermissions = EzArray.EMPTY;
    /**
     * @public @readonly @property
     * Gets an array of the default disabled permissions
     * @returns {array}
     */
    get ezDefaultDisabledPermissions() {
        return this.#ezDefaultDisabledPermissions;
    }
    /**
     * @public @function
     * Gets the the list of default disabled permissions for this role.
     * @returns {array}
     * @deprecated
     * Migrate to property: EzClockerRole.ezDefaultDisabledPermissions
     */
    ezGetDefaultDisabledPermissions() {
        return this.ezDefaultDisabledPermissions;
    }

    /**
     * @public @readonly @property
     * Gets this instance as a JSON Object (will no longer have the getter/setter methods).
     * Useful for serailization to JSON.
     * @returns {object}
     * Returns this instance serialized to a JSON object.
     * If unable to transform this instance to a JSON object the following error JSON object is returned instead:
     * {
     *      serializationError: EzString.stringWithTextOrDefault(
     *          err?.message,
     *          'Unknown error during JSON serialization.'),
     *
     *      toString: this.toString()
     *  }
     */
    get asJSONObject() {
        return {
            authorityName: this.authorityName,
            roleName: this.roleName,
            contextName: this.contextName,
            accessInfo: this.accessInfo,
            internalOnly: this.internalOnly,
            legacyAccountType: this.legacyAccountType,
            enabledPermissions: this.ezDefaultEnabledPermissions,
            disabledPermissions: this.ezDefaultDisabledPermissions
        };
    }

    /**
     * @public @function
     * Copies properties from the provided jsonObject to this instance (even if this instance does not define the property)
     * @param {object} jsonObject
     * @returns {object}
     * Returns this instance with the key & values from the provided jsonObject.
     */
    ezFromJSONObject(jsonObject) {
        if (!EzObject.isValid(jsonObject)) {
            return this;
        }

        this.authorityName = EzObject.assignOrDefault(
            jsonObject.authorityName,
            this.authorityName);

        this.roleName = EzObject.assignOrDefault(
            jsonObject.roleName,
            this.roleName);

        this.contextName = EzObject.assignOrDefault(
            jsonObject.contextName,
            this.contextName);

        this.accessInfo = EzObject.assignOrDefault(
            jsonObject.accessInfo,
            this.accessInfo);

        this.internalOnly = EzObject.assignOrDefault(
            jsonObject.internalOnly,
            this.internalOnly);

        this.legacyAccountType = EzObject.assignOrDefault(
            jsonObject.legacyAccountType,
            this.legacyAccountType);

        this.enabledPermissions = EzObject.assignOrDefault(
            jsonObject.ezDefaultEnabledPermissions,
            this.ezDefaultEnabledPermissions);

        this.disabledPermissions = EzObject.assignOrDefault(
            jsonObject.ezDefaultDisabledPermissions,
            this.ezDefaultDisabledPermissions);

        return this;
    }
}
